import { closeDim, openDim } from './dim';
import { handleMobileSlider } from './sliders';

const overlayCart = document.querySelector('.js-overlayCart');
const toggleOverlayCart = document.querySelectorAll('.js-toggleOverlayCart');

const CART_LOCK_TIMEOUT = 8000;

let cartActionInFlight = false;
let cartLockSafetyTimer = null;
let lastClickedButton = null;

// Track in-flight wc-ajax=get_refreshed_fragments / get_cart_totals requests so
// we can abort them when a new cart mutation starts. Without this, a stale
// fragment-refresh response (e.g. from Safari pageshow or cross-tab storage
// events that trigger wc_fragment_refresh) can land *after* a newer mutation
// and re-render the just-removed item.
const pendingFragmentRefreshXhrs = new Set();

function abortPendingFragmentRefresh() {
    pendingFragmentRefreshXhrs.forEach(function(xhr) {
        try { xhr.abort('superseded by new cart mutation'); } catch (e) {}
    });
    pendingFragmentRefreshXhrs.clear();
}

if (typeof jQuery !== 'undefined') {
    jQuery(document).ajaxSend(function(event, jqxhr, settings) {
        if (!settings || typeof settings.url !== 'string') return;
        if (settings.url.indexOf('wc-ajax=get_refreshed_fragments') === -1
            && settings.url.indexOf('wc-ajax=get_cart_totals') === -1) {
            return;
        }
        pendingFragmentRefreshXhrs.add(jqxhr);
    });

    jQuery(document).ajaxComplete(function(event, jqxhr) {
        pendingFragmentRefreshXhrs.delete(jqxhr);
    });
}

function setCartLock(originButton) {
    cartActionInFlight = true;
    document.body.classList.add('-cartActionInFlight');
    if (overlayCart) {
        overlayCart.classList.add('-processing');
    }
    if (originButton) {
        originButton.classList.add('loading');
        originButton.setAttribute('aria-busy', 'true');
    }
    if (cartLockSafetyTimer) {
        clearTimeout(cartLockSafetyTimer);
    }
    cartLockSafetyTimer = setTimeout(releaseCartLock, CART_LOCK_TIMEOUT);
}

function releaseCartLock() {
    cartActionInFlight = false;
    document.body.classList.remove('-cartActionInFlight');
    if (overlayCart) {
        overlayCart.classList.remove('-processing');
    }
    if (cartLockSafetyTimer) {
        clearTimeout(cartLockSafetyTimer);
        cartLockSafetyTimer = null;
    }
    document
        .querySelectorAll('.remove_from_cart_button.loading, .add_to_cart_button.loading, .single_add_to_cart_button.loading')
        .forEach(function(el) {
            el.classList.remove('loading');
            el.removeAttribute('aria-busy');
        });
}

// -----------------------------------------------------------------------------
// Cart action queue
//
// We take over click handling for all mini-cart-mutating buttons (remove and
// ajax-add) so we can serialise them strictly: one in-flight at a time, FIFO.
// The capture-phase listener below ALWAYS calls preventDefault +
// stopImmediatePropagation for those buttons so WooCommerce's own
// AddToCartHandler never fires its own AJAX. Our queue then drives the
// AJAX requests, applies fragments, and triggers the standard WC events
// (added_to_cart / removed_from_cart / wc_fragments_loaded) so 3rd-party
// listeners (analytics, GTM, our minicart counter) keep working.
//
// Why a queue instead of relying on WC's own addRequest serialisation:
// -  WC's queue still allows pre/post-AJAX handler order races with
//    plugins/themes; ours owns the entire flow end-to-end.
// -  Lock release happens deterministically AFTER fragments are applied
//    and the standard events have fired, so the next mutation can never
//    overlap with stale fragment data.
// -  Single-product-page add-to-cart (.single_add_to_cart_button) is left
//    to the woo-ajax-add-to-cart plugin; the queue only intercepts the
//    minicart/loop buttons.
// -----------------------------------------------------------------------------

const cartActionQueue = [];
let processingCartQueue = false;

function enqueueCartAction(action) {
    if (action.button.classList.contains('-queued')) {
        return; // double-click on the same button: ignore
    }
    action.button.classList.add('-queued');
    cartActionQueue.push(action);
    processCartQueue();
}

function processCartQueue() {
    if (processingCartQueue) return;
    if (cartActionQueue.length === 0) return;

    processingCartQueue = true;
    const action = cartActionQueue.shift();
    setCartLock(action.button);
    abortPendingFragmentRefresh();

    const promise = action.type === 'remove'
        ? performRemoveFromCart(action.button)
        : performAddToCart(action.button);

    Promise.resolve(promise)
        .catch(function() { /* swallow; we always want to release the lock */ })
        .then(function() {
            action.button.classList.remove('-queued');
            releaseCartLock();
            processingCartQueue = false;
            processCartQueue();
        });
}

function performRemoveFromCart(button) {
    return new Promise(function(resolve) {
        if (typeof wc_add_to_cart_params === 'undefined') {
            // WC frontend params not loaded; fall back to default href navigation.
            window.location = button.getAttribute('href') || window.location.href;
            return;
        }

        const $button = jQuery(button);
        const cartItemKey = button.dataset.cart_item_key || $button.data('cart_item_key');
        if (!cartItemKey) { resolve(); return; }

        const $row = $button.closest('.woocommerce-mini-cart-item');
        if ($row.length && typeof $row.block === 'function') {
            $row.fadeTo(200, 0.6).block({
                message: null,
                overlayCSS: { opacity: 0.6 }
            });
        }

        jQuery.ajax({
            type: 'POST',
            url: wc_add_to_cart_params.wc_ajax_url.toString().replace('%%endpoint%%', 'remove_from_cart'),
            data: { cart_item_key: cartItemKey },
            dataType: 'json',
            success: function(response) {
                if (!response || !response.fragments) {
                    window.location = button.getAttribute('href') || window.location.href;
                    return;
                }
                applyCartFragments(response.fragments, response.cart_hash);
                jQuery(document.body).trigger('removed_from_cart',
                    [response.fragments, response.cart_hash, $button]);
                resolve();
            },
            error: function() {
                window.location = button.getAttribute('href') || window.location.href;
            }
        });
    });
}

function performAddToCart(button) {
    return new Promise(function(resolve) {
        if (typeof wc_add_to_cart_params === 'undefined') {
            resolve();
            return;
        }

        const $button = jQuery(button);
        $button.removeClass('added').addClass('loading');

        if (false === jQuery(document.body).triggerHandler('should_send_ajax_request.adding_to_cart', [$button])) {
            jQuery(document.body).trigger('ajax_request_not_sent.adding_to_cart', [false, false, $button]);
            $button.removeClass('loading');
            resolve();
            return;
        }

        const data = {};
        jQuery.each($button.data(), function(key, value) { data[key] = value; });
        jQuery.each(button.dataset, function(key, value) { data[key] = value; });

        jQuery(document.body).trigger('adding_to_cart', [$button, data]);

        jQuery.ajax({
            type: 'POST',
            url: wc_add_to_cart_params.wc_ajax_url.toString().replace('%%endpoint%%', 'add_to_cart'),
            data: data,
            dataType: 'json',
            success: function(response) {
                if (!response) { resolve(); return; }
                if (response.error && response.product_url) {
                    window.location = response.product_url;
                    return;
                }
                if (wc_add_to_cart_params.cart_redirect_after_add === 'yes') {
                    window.location = wc_add_to_cart_params.cart_url;
                    return;
                }
                if (response.fragments) {
                    applyCartFragments(response.fragments, response.cart_hash);
                }
                lastClickedButton = button;
                jQuery(document.body).trigger('added_to_cart',
                    [response.fragments, response.cart_hash, $button]);
                resolve();
            },
            error: function() {
                $button.removeClass('loading');
                resolve();
            }
        });
    });
}

function applyCartFragments(fragments, cart_hash) {
    if (!fragments) return;

    jQuery.each(fragments, function(key) {
        jQuery(key).addClass('updating').fadeTo(200, 0.6);
    });
    jQuery.each(fragments, function(key, value) {
        jQuery(key).replaceWith(value);
        jQuery(key).stop(true).css('opacity', '1');
    });

    if (typeof wc_cart_fragments_params !== 'undefined' && window.sessionStorage) {
        try {
            sessionStorage.setItem(wc_cart_fragments_params.fragment_name, JSON.stringify(fragments));
            if (cart_hash) {
                sessionStorage.setItem(wc_cart_fragments_params.cart_hash_key, cart_hash);
                if (window.localStorage) {
                    localStorage.setItem(wc_cart_fragments_params.cart_hash_key, cart_hash);
                }
            }
        } catch (e) { /* storage full or disabled, ignore */ }
    }

    jQuery(document.body).trigger('wc_fragments_loaded');
}

// Capture-phase listener: fires BEFORE any jQuery delegated bubble handler
// (including WooCommerce's add-to-cart.js), regardless of script load order.
// We unconditionally suppress WC's own click handling for cart-mutating
// buttons and route the action through our queue.
document.body.addEventListener(
    'click',
    function(e) {
        const removeBtn = e.target.closest('.remove_from_cart_button');
        if (removeBtn) {
            e.preventDefault();
            e.stopImmediatePropagation();
            e.stopPropagation();
            enqueueCartAction({ type: 'remove', button: removeBtn });
            return;
        }

        const addBtn = e.target.closest('.add_to_cart_button:not(.single_add_to_cart_button):not(.wc-interactive)');
        if (addBtn && addBtn.classList.contains('ajax_add_to_cart') && (addBtn.dataset.product_id || addBtn.getAttribute('data-product_id'))) {
            e.preventDefault();
            e.stopImmediatePropagation();
            e.stopPropagation();
            enqueueCartAction({ type: 'add', button: addBtn });
            return;
        }

        // Single-product-page add-to-cart is handled by woo-ajax-add-to-cart.
        // We only track the button here so the added_to_cart handler can show
        // "Toegevoegd" feedback. The cart lock is set later from the
        // adding_to_cart jQuery event so that a failed client-side validation
        // (which preventDefaults the click before any AJAX is sent) does not
        // leave the button stuck in a loading state.
        const singleAddBtn = e.target.closest('.single_add_to_cart_button');
        if (singleAddBtn) {
            lastClickedButton = singleAddBtn;
        }
    },
    true
);

document.addEventListener("DOMContentLoaded", function() {

    toggleOverlayCart.forEach( element => {
        element.addEventListener('click', function(e) {
            e.preventDefault();
            if (overlayCart.classList.contains('-open')) {
                closeOverlayCart();
            } else {
                openOverlayCart();
            }
        })
    })

    jQuery(document).on('click', '.js-closeOverlayCart', function(){
        closeOverlayCart();
    });

});

let overlayCartTimeout;
export function openOverlayCart(addedToCart = false) {
    document.querySelector('body').classList.add('-overlayCartOpen');
    overlayCart.classList.add('-open');

    openDim();

    if (addedToCart) {
        overlayCart.classList.remove('-addedToCart');
        overlayCart.classList.remove('-message');
        overlayCart.querySelector('.js-overlayCartMessage .bx').classList.remove('bx-tada');

        if (overlayCartTimeout) {
            clearTimeout(overlayCartTimeout);
        }

        setTimeout(function() {
            overlayCart.classList.add('-addedToCart');
            overlayCart.classList.add('-message');
            overlayCart.querySelector('.js-overlayCartMessage .bx').classList.add('bx-tada');
        }, 100);

        overlayCartTimeout = setTimeout(function() {
            overlayCart.classList.remove('-addedToCart');
            overlayCart.classList.remove('-message');
            overlayCart.querySelector('.js-overlayCartMessage .bx').classList.remove('bx-tada');
        }, 5000);
    }
}

export function closeOverlayCart() {
    document.querySelector('body').classList.remove('-overlayCartOpen');
    overlayCart.classList.remove('-open');
    const singleAddToCartButtons = document.querySelectorAll('.added');
    singleAddToCartButtons.forEach(button => {
        button.classList.remove('added');
    });

    closeDim();
}

function updateCartCounter() {
    let cartCount = 0;
    const $counters = jQuery('.js-cartCounter');

    jQuery('.woocommerce-mini-cart .mini_cart_item').each(function() {
        const $quantity = jQuery(this).find('.quantity');
        const quantity = $quantity.length ? parseInt($quantity.text().replace(/[^\d]/g, '')) : 1;
        cartCount += quantity;
    });

    $counters.each(function() {
        jQuery(this).text(cartCount);
        jQuery(this).attr('data-count', cartCount);
    });
}

const observer = new MutationObserver(function(mutationsList, observer) {
    mutationsList.forEach(function(mutation) {
        if (mutation.type === 'childList') {
            updateCartCounter();
        }
    });
});

const cartContainer = document.querySelector('.js-overlayCartItems');

if (cartContainer) {
    observer.observe(cartContainer, { childList: true, subtree: true });
}

// woo-ajax-add-to-cart triggers `adding_to_cart` after its own click handler
// has decided to send the AJAX request. By that point, the form-level
// validation in woo-custom-product-fields has already run; if it failed
// (preventDefault + stopImmediatePropagation), this event never fires, so the
// cart lock / loading state is never applied for that click.
jQuery(document.body).on('adding_to_cart', function(event, $button) {
    if (!$button || !$button.length) return;
    if (!$button[0].classList.contains('single_add_to_cart_button')) return;
    if (cartActionInFlight) return;
    lastClickedButton = $button[0];
    abortPendingFragmentRefresh();
    setCartLock($button[0]);
});

// removed_from_cart / added_to_cart handlers run AFTER applyCartFragments has
// updated the DOM. They no longer release the cart lock -- the queue does
// that once the AJAX promise has settled. These handlers only do
// minicart-open / checkout update side-effects.
jQuery(document.body).on('removed_from_cart', function() {
    if (document.body.classList.contains('woocommerce-checkout')) {
        jQuery('body').trigger('update_checkout');
    }
});

jQuery(document.body).on('added_to_cart', function() {
    const shouldOpen = !lastClickedButton || !lastClickedButton.closest('[data-no-minicart]');
    if (shouldOpen) {
        openOverlayCart(true);
    } else if (lastClickedButton) {
        const $btn = jQuery(lastClickedButton);
        const originalText = $btn.text();
        $btn.text('Toegevoegd');

        setTimeout(function() {
            $btn.text(originalText);
            $btn.removeClass('added');
        }, 3000);
    }

    lastClickedButton = null;
    handleMobileSlider();

    if (document.body.classList.contains('woocommerce-checkout')) {
        jQuery('body').trigger('update_checkout');
    }
});

// Secondary safety net: if wc_fragment_refresh is fired by something we don't
// own (Safari pageshow, cross-tab storage event, woo-ajax-add-to-cart on
// single product pages), make sure the lock is released when the resulting
// fragments are loaded / refreshed / cart emptied.
jQuery(document.body).on('wc_fragments_refreshed wc_fragments_loaded wc_cart_emptied', function() {
    if (!processingCartQueue) {
        releaseCartLock();
    }
});

jQuery(document).ajaxError(function(event, jqxhr, settings) {
    if (settings && typeof settings.url === 'string' && settings.url.indexOf('wc-ajax=') !== -1) {
        if (!processingCartQueue) {
            releaseCartLock();
        }
    }
});
